import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskCategoriesAPI, helpdeskTicketsAPI } from '../../lib/api';
import { AREAS, normalizeArea, getAreaColor } from '../../lib/helpdesk-constants';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  Tag,
  Building,
  Ticket,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const HelpdeskCategories = () => {
  const { isAdmin } = useAuth();
  const { canAccess, canWrite: canWritePerm } = usePermissions();
  // Access page if admin or user/viewer with at least 'basic' on mesa_de_ayuda.categorias
  const canViewCategories = isAdmin() || canAccess('mesa_de_ayuda', 'categorias');
  // Write (create/edit/delete) requires 'full'
  const canWriteCategories = isAdmin() || canWritePerm('mesa_de_ayuda', 'categorias');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    area: '', // Uses AREAS from helpdesk-constants
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Redirect users without any access
  useEffect(() => {
    if (!canViewCategories) {
      toast.error('No tienes permisos para acceder a esta sección');
      navigate('/helpdesk');
    }
  }, [canViewCategories, navigate]);

  useEffect(() => {
    if (canViewCategories) {
      fetchData();
    }
  }, [canViewCategories]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, ticketsRes] = await Promise.all([
        helpdeskCategoriesAPI.getAll(),
        helpdeskTicketsAPI.getAll(),
      ]);
      setCategories(categoriesRes.data);
      setTickets(ticketsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything for users without view access
  if (!canViewCategories) {
    return null;
  }

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        // Normalize legacy values when editing - check area first, then legacy fields
        area: normalizeArea(category.area || category.department || ''),
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', area: '' });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.name.trim() || !categoryForm.area.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    setSaving(true);
    try {
      // Send area for backend
      const payload = {
        name: categoryForm.name,
        area: categoryForm.area,
      };
      
      if (editingCategory) {
        await helpdeskCategoriesAPI.update(editingCategory.id, payload);
        toast.success('Categoría actualizada');
      } else {
        await helpdeskCategoriesAPI.create(payload);
        toast.success('Categoría creada');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      await helpdeskCategoriesAPI.delete(categoryToDelete.id);
      toast.success('Categoría desactivada');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al desactivar la categoría');
    }
  };

  // Calculate stats per category
  const getCategoryStats = (categoryId) => {
    const categoryTickets = tickets.filter(t => t.category_id === categoryId);
    return {
      total: categoryTickets.length,
      open: categoryTickets.filter(t => t.status !== 'closed').length,
      closed: categoryTickets.filter(t => t.status === 'closed').length,
    };
  };

  // Get stats by area (normalize legacy values)
  const areaStats = categories.reduce((acc, cat) => {
    const stats = getCategoryStats(cat.id);
    // Normalize area - handles legacy field names and values
    const area = normalizeArea(cat.area || cat.department) || 'Sin área';
    if (!acc[area]) {
      acc[area] = { total: 0, open: 0, closed: 0 };
    }
    acc[area].total += stats.total;
    acc[area].open += stats.open;
    acc[area].closed += stats.closed;
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-800">
              Categorías
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Gestiona las categorías y departamentos de tickets
            </p>
          </div>
          {canWriteCategories && (
            <Button onClick={() => handleOpenDialog()} data-testid="create-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Categoría
            </Button>
          )}
        </div>

        {/* Area Stats */}
        {Object.keys(areaStats).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(areaStats).map(([area, stats]) => (
              <Card key={area}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-sm">{area}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold">{stats.total}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-600">{stats.open}</p>
                      <p className="text-xs text-slate-500">Abiertos</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600">{stats.closed}</p>
                      <p className="text-xs text-slate-500">Cerrados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Categories List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">
              Categorías ({categories.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categories.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No hay categorías</p>
                <p className="text-sm text-slate-400 mt-1">
                  Crea una categoría para comenzar a organizar los tickets
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {categories.map(category => {
                  const stats = getCategoryStats(category.id);
                  
                  return (
                    <div
                      key={category.id}
                      className={cn(
                        "border rounded-lg p-4 transition-colors",
                        !category.is_active && "bg-slate-50 opacity-60"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Tag className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-slate-800">{category.name}</h3>
                              {!category.is_active && (
                                <Badge variant="outline" className="text-xs text-slate-500">
                                  Inactiva
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Building className="w-3 h-3" />
                              {/* Normalize area - handles legacy field names */}
                              {normalizeArea(category.area || category.department)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          {/* Stats */}
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <p className="font-bold">{stats.total}</p>
                              <p className="text-xs text-slate-500">Total</p>
                            </div>
                            <div className="text-center">
                              <p className="font-bold text-blue-600">{stats.open}</p>
                              <p className="text-xs text-slate-500">Abiertos</p>
                            </div>
                            <div className="text-center">
                              <p className="font-bold text-emerald-600">{stats.closed}</p>
                              <p className="text-xs text-slate-500">Cerrados</p>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          {canWriteCategories && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(category)}
                                data-testid={`edit-category-${category.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {category.is_active && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setCategoryToDelete(category);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-category-${category.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-rose-500" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory ? 'Modifica los datos de la categoría' : 'Crea una nueva categoría para tickets'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Impresoras, Red, ERP"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area">Área *</Label>
                  <Select
                    value={categoryForm.area}
                    onValueChange={(v) => setCategoryForm({ ...categoryForm, area: v })}
                  >
                    <SelectTrigger data-testid="category-area-select">
                      <SelectValue placeholder="Selecciona un área" />
                    </SelectTrigger>
                    <SelectContent>
                      {AREAS.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-category-btn">
                  {saving ? 'Guardando...' : editingCategory ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Desactivar categoría?</AlertDialogTitle>
              <AlertDialogDescription>
                La categoría "{categoryToDelete?.name}" será desactivada. Los tickets existentes conservarán su categoría.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">
                Desactivar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default HelpdeskCategories;
